import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Paper, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useToast } from '../../context/ToastContext';
import { InstitutionGroupService } from '../../services/institutionGroup.service';
import { InstitutionService } from '../../services/institution.service';
import { GroupMemberRole, InstitutionGroup } from '../../interfaces/intercompany.interface';
import { IInstitution } from '../../interfaces/institution.interface';
import { SearchableSelect } from '../common/SearchableSelect';
import { humanizeEnum } from '../../utils/format';
import GroupAccessDialog from '../institutions/GroupAccessDialog';

const ROLES: GroupMemberRole[] = ['PARENT', 'SUBSIDIARY', 'ASSOCIATE'];

/** Platform-only (SystemAdmin / SaaS operator): institution groups for inter-company and consolidation. */
const InstitutionGroups: React.FC = () => {
  const toast = useToast();
  const [groups, setGroups] = useState<InstitutionGroup[]>([]);
  const [institutions, setInstitutions] = useState<IInstitution[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [month, setMonth] = useState('1');
  const [notes, setNotes] = useState('');
  const [memberFor, setMemberFor] = useState<InstitutionGroup | null>(null);
  const [instId, setInstId] = useState('');
  const [role, setRole] = useState<GroupMemberRole>('SUBSIDIARY');
  const [pct, setPct] = useState('');
  const [saving, setSaving] = useState(false);
  // polish 6: grant a sister company's people access in this member
  const [accessFor, setAccessFor] = useState<{ id: string; name: string } | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setGroups(await InstitutionGroupService.list());
    } catch (e) {
      toast.showApiError(e, 'Failed to load institution groups');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load();
    InstitutionService.getInstitutions(0, 200).then(r => setInstitutions(r?.content ?? [])).catch(e => toast.showApiError(e, 'Failed to load institutions'));
  }, [load]); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async () => {
    try {
      setSaving(true);
      await InstitutionGroupService.create({ name: name.trim(), fiscalYearStartMonth: Number(month), notes: notes || undefined });
      setCreateOpen(false);
      await load();
    } catch (e) {
      toast.showApiError(e, 'Failed to create the group');
    } finally {
      setSaving(false);
    }
  };

  const addMember = async () => {
    if (!memberFor) return;
    try {
      setSaving(true);
      await InstitutionGroupService.addMember(memberFor.id, { institutionId: instId, memberRole: role, ownershipPct: pct === '' ? null : Number(pct) });
      setMemberFor(null);
      await load();
    } catch (e) {
      toast.showApiError(e, 'Failed to add the member');
    } finally {
      setSaving(false);
    }
  };

  const removeMember = async (g: InstitutionGroup, memberId: string) => {
    try {
      await InstitutionGroupService.removeMember(g.id, memberId);
      await load();
    } catch (e) {
      toast.showApiError(e, 'Failed to remove the member');
    }
  };

  return (
    <Box sx={{ p: 2 }} data-help-page="saas.groups.page">
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
        <Typography variant="h5" sx={{ flex: '1 1 240px' }}>Institution Groups</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ flex: '2 1 320px' }}>
          Click a member company to give people from the group's other companies a role in it.
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} data-help="saas.groups.new" onClick={() => { setName(''); setMonth('1'); setNotes(''); setCreateOpen(true); }}>New group</Button>
      </Box>
      {loading ? <CircularProgress /> : (
        <TableContainer component={Paper} data-help-section="saas.groups.table">
          <Table size="small">
            <TableHead>
              <TableRow><TableCell data-help="saas.groups.col.group">Group</TableCell><TableCell data-help="saas.groups.col.fiscal">Fiscal year starts (month)</TableCell><TableCell data-help="saas.groups.col.members">Members</TableCell><TableCell data-help="saas.groups.col.actions" /></TableRow>
            </TableHead>
            <TableBody>
              {groups.length === 0 && <TableRow><TableCell colSpan={4}>No groups yet.</TableCell></TableRow>}
              {groups.map(g => (
                <TableRow key={g.id}>
                  <TableCell>{g.name}</TableCell>
                  <TableCell>{g.fiscalYearStartMonth}</TableCell>
                  <TableCell>
                    {g.members.map(m => (
                      <Chip key={m.id} sx={{ mr: 0.5, mb: 0.5 }} label={`${m.institutionName} (${humanizeEnum(m.memberRole)}${m.ownershipPct != null ? ` ${m.ownershipPct}%` : ''})`}
                        title="Manage access from the other companies of this group" data-help="saas.groups.member"
                        onClick={() => setAccessFor({ id: m.institutionId, name: m.institutionName })}
                        onDelete={() => removeMember(g, m.id)} deleteIcon={<DeleteIcon aria-label={`Remove ${m.institutionName}`} />} />
                    ))}
                  </TableCell>
                  <TableCell><Button size="small" data-help="saas.groups.action.addMember" onClick={() => { setMemberFor(g); setInstId(''); setRole('SUBSIDIARY'); setPct(''); }}>Add member</Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {accessFor && (
        <GroupAccessDialog open onClose={() => setAccessFor(null)} institutionId={accessFor.id} institutionName={accessFor.name} />
      )}

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth data-help-page="saas.groups.createDialog">
        <DialogTitle>New group</DialogTitle>
        <DialogContent data-help-section="saas.groups.createDialog.section.group" sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField data-help="saas.groups.field.name" size="small" label="Name" required value={name} onChange={e => setName(e.target.value)} />
          <TextField data-help="saas.groups.field.fiscalMonth" select size="small" label="Fiscal year start month" value={month} onChange={e => setMonth(e.target.value)}
            helperText="Every member must share this month.">
            {Array.from({ length: 12 }, (_, i) => <MenuItem key={i + 1} value={String(i + 1)}>{i + 1}</MenuItem>)}
          </TextField>
          <TextField data-help="saas.groups.field.notes" size="small" label="Notes" value={notes} onChange={e => setNotes(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button data-help="saas.groups.createDialog.cancel" onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button data-help="saas.groups.createDialog.save" variant="contained" disabled={!name.trim() || saving} onClick={create}>Create</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!memberFor} onClose={() => setMemberFor(null)} maxWidth="sm" fullWidth data-help-page="saas.groups.memberDialog">
        <DialogTitle>Add member to {memberFor?.name}</DialogTitle>
        <DialogContent data-help-section="saas.groups.memberDialog.section.member" sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <SearchableSelect data-help="saas.groups.field.institution" options={institutions} value={instId} onChange={id => setInstId(id)} label="Institution" size="small"
            getOptionLabel={i => `${i.name} (${i.code})`} />
          <TextField data-help="saas.groups.field.role" select size="small" label="Role (memo)" value={role} onChange={e => setRole(e.target.value as GroupMemberRole)}>
            {ROLES.map(r => <MenuItem key={r} value={r}>{humanizeEnum(r)}</MenuItem>)}
          </TextField>
          <TextField data-help="saas.groups.field.ownership" size="small" type="number" label="Ownership % (memo)" value={pct} onChange={e => setPct(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button data-help="saas.groups.memberDialog.cancel" onClick={() => setMemberFor(null)}>Cancel</Button>
          <Button data-help="saas.groups.memberDialog.save" variant="contained" disabled={!instId || saving} onClick={addMember}>Add</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InstitutionGroups;
